import React, { useCallback, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import { addFollower } from "api/requests/addFollower";
import { removeFollower } from "api/requests/removeFollower";
import { Action, Category, Label } from "constants/events";
import { Brand } from "custom-types/Brand";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";

import FavoriteButton from "components/botanic/FavoriteButton";
import Tab from "components/botanic/Tab";
import Tabs from "components/botanic/Tabs";
import Image from "components/Image";

import BrandLogoHeading from "../BrandLogoHeading";
import BrandPhotoCarousel from "../BrandPhotoCarousel";

const BrandHeader = ({
  brand,
  isFollowing,
}: {
  brand: Brand;
  isFollowing?: boolean;
}) => {
  const [brandFollowerCount, setBrandFollowerCount] = useState<number>(
    brand?.followerCount || 0
  );
  const [brandFavorited, setBrandIsFavorited] = useState(isFollowing);
  const countryCode = useDomainCountryCode();
  const { publishEvent } = useEventTracker();
  const showPhotoCarousel =
    brand.brandFeatureTier === "paid" && !!brand.photos.length;

  const handleFavoriteClick = useCallback(async () => {
    publishEvent({
      action: Action.click,
      brandId: brand.id,
      category:
        brand.brandFeatureTier === "paid"
          ? Category.brandPaidProfile
          : Category.brandInfo,
      label: brandFavorited ? Label.unfollowButton : Label.followButton,
    });

    const followerCall = brandFavorited ? removeFollower : addFollower;

    const success = await followerCall({
      countryCode,
      followeeId: brand.id,
      followeeType: "brand",
    });

    if (success) {
      setBrandIsFavorited(!brandFavorited);
      followerCall === addFollower
        ? setBrandFollowerCount((count) => count + 1)
        : setBrandFollowerCount((count) => count - 1);
    }
  }, [brandFavorited]);

  const handleTabClick = useCallback(
    (tab: string) => {
      const category =
        brand.brandFeatureTier === "paid"
          ? Category.brandPaid
          : Category.brandInfo;

      publishEvent({
        action: Action.click,
        brandId: brand.id,
        category,
        label: `tab ${tab}`,
      });
    },
    [brand]
  );

  const Router = useRouter();

  const activeTab = useMemo(() => {
    if (Router.asPath.includes("/catalog")) {
      return "catalog";
    }
    return "about";
  }, [Router.asPath]);

  return (
    <div className="flex flex-col gap-xl">
      {showPhotoCarousel ? (
        <BrandPhotoCarousel photos={brand.photos} imageSizing={true} />
      ) : (
        <Image
          alt="Leafly default photo banner"
          sizes={[400, 600, 800, 1000, null, 800]}
          src={brand.bannerPhoto}
          disableLazyLoad
        />
      )}
      <div className="flex justify-between gap-sm">
        <BrandLogoHeading
          name={brand.name}
          claimed={brand.claimed}
          tagline={brand.tagline}
          logo={brand.logo}
          id={brand.id}
        />
        <FavoriteButton
          onClick={handleFavoriteClick}
          className="h-xxl"
          isFavorited={!!brandFavorited}
          label={brandFollowerCount}
          data-testid="brand-favorite"
        />
      </div>
      {!brand.claimed && (
        <div className="flex flex-1 items-start justify-center bg-leafly-white">
          <p className="p-lg mb-0">This brand is currently unclaimed</p>
        </div>
      )}
      <Tabs className="pt-0.5" ariaLabel="Brands page">
        <Tab
          as={Link}
          href={`/brands/${brand.slug}`}
          onClick={() => handleTabClick("about")}
          active={activeTab === "about"}
        >
          About
        </Tab>
        <Tab
          as={Link}
          href={`/brands/${brand.slug}/catalog`}
          onClick={() => handleTabClick("catalog")}
          active={activeTab === "catalog"}
        >
          Catalog
        </Tab>
      </Tabs>
    </div>
  );
};

export default BrandHeader;
